// ./src/components/features/EventTheme/ThemeLookSections.tsx
//
// THE THEME'S LOOK, AS EDITABLE SECTIONS — the page / text / accent / fonts and corners / buttons / links panels of the
// theme editor (ROADMAP A2e #89), shared since B20 (owner, 2026-10-15) with an EVENT's Theme page, where they adjust the
// chosen theme for that one event. Accordion ITEMS only: the caller owns the `Accordion` (and adds its own items — the
// editor's name, block colours and pictures).
//
// Two modes:
//   • THEME (the editor): the values are the theme's own; a role colour can be cleared (the site falls back to its
//     foundation colour).
//   • EVENT (`baseTokens` given): the values are the theme WITH the event's adjustments; every adjusted value is marked
//     and has "Use the theme's" (it is removed from the event's overrides — the server drops a cleared leaf, so an event
//     cannot blank a theme's colour, only take its own adjustment off). A section holding adjustments says so closed.
//
// The rules stay the server's (eventsmagic-api `boundary/theme.ts`): a refusal is keyed on its token path and shown AT
// its control (`fieldErrors`); `THEME_LOOK_FIELDS` is the list a page passes to `getFormErrors`.
import React from "react";
import { Accordion, ActionIcon, Badge, Box, Group, Input, SimpleGrid, Slider, Stack, Text, Tooltip } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { BLOCK_TEXT_SIZES, themeFontKeyOfStack, themeFontOf, themeFontSelectData, type ThemeFontRole } from "@em/blocks/blockFonts";

import type { ThemeTokens } from "@/utils/api";
import { applyFoundationChange, getTokenPath, setTokenPath } from "@/utils/themeTokens";
import { AppIcon, ColorPickerInput, SegmentedField, SelectField } from "@/components/generic";

/** The look sections and the server fields shown in each — a section holding a refusal opens itself. */
export const THEME_LOOK_SECTION_FIELDS: Record<string, string[]> = {
  page: ["color.background", "color.surface", "color.surfaceAlt"],
  text: ["color.ink", "color.inkSoft", "color.muted", "color.line", "color.lineStrong"],
  accent: ["color.accent", "color.accentInk", "color.accentWash", "color.accentLine"],
  type: ["font.display", "element.title.family", "font.body", "font.headingSize", "shape.radius"],
  buttons: ["button.primary.bg", "button.primary.text", "button.secondary.bg", "button.secondary.text", "button.secondary.border"],
  links: ["element.link.color", "element.eyebrow.color"],
  media: ["effects.media"],
};

/** Every field these sections render — for a page's KNOWN_FORM_FIELDS. ⚠ Each is read at its control below. */
export const THEME_LOOK_FIELDS: string[] = Object.values(THEME_LOOK_SECTION_FIELDS).flat();

const RADIUS_MAX = 40;
const CUSTOM_FONT = "__custom";

export const radiusOf = (tokens: ThemeTokens): number => {
  const n = parseFloat(String(tokens.shape?.radius ?? ""));

  return Number.isFinite(n) ? Math.min(Math.max(Math.round(n), 0), RADIUS_MAX) : 12;
};

export interface ThemeLookSectionsProps {
  /** What is shown: the theme's tokens (editor), or the theme with the event's adjustments over it (event). */
  tokens: ThemeTokens;
  onChange: (next: ThemeTokens) => void;
  /** Read-only (no plan, no rights). ⓘ In event mode "Use the theme's" stays available: removing is always allowed. */
  locked: boolean;
  fieldErrors: Record<string, string | undefined>;
  /** EVENT mode: the theme's own tokens — adjusted values are marked and can go back to these. */
  baseTokens?: ThemeTokens | null;
}

export const ThemeLookSections: React.FC<ThemeLookSectionsProps> = ({ tokens, onChange, locked, fieldErrors, baseTokens }) => {
  const { t } = useTranslation();
  const eventMode = baseTokens !== undefined;

  const valueAt = (path: string): string | null => {
    const v = getTokenPath(tokens, path.split("."));

    return typeof v === "string" ? v : null;
  };
  const baseAt = (path: string): string | null => {
    const v = getTokenPath(baseTokens, path.split("."));

    return typeof v === "string" ? v : null;
  };
  const adjusted = (path: string): boolean => eventMode && JSON.stringify(getTokenPath(tokens, path.split("."))) !== JSON.stringify(getTokenPath(baseTokens, path.split(".")));
  const sectionAdjusted = (key: string): boolean => eventMode && THEME_LOOK_SECTION_FIELDS[key].some(adjusted);

  const setColor = (path: string, value: string | null) => onChange(applyFoundationChange(tokens, path, value));
  const setRole = (path: string, value: string | null) => onChange(setTokenPath(tokens, path.split("."), value));
  /** Back to the theme's value — a foundation colour carries its linked roles back with it. */
  const reset = (path: string, foundation: boolean) => (foundation ? setColor(path, baseAt(path)) : setRole(path, baseAt(path)));

  /** The label row: the name, then — event mode — "Use the theme's" on an adjusted value, else (editor) Clear on a role. */
  const labelRow = (path: string, label: string, foundation: boolean) => (
    <Group gap={6} mb={4} wrap="nowrap">
      <Text fw={500} size="md">
        {label}
      </Text>
      {adjusted(path) && (
        <Tooltip label={t("eventTheme:useThemes", { defaultValue: "Use the theme's" })} withArrow>
          <ActionIcon size="md" variant="subtle" color="gray" onClick={() => reset(path, foundation)} aria-label={t("eventTheme:useThemes", { defaultValue: "Use the theme's" })}>
            <AppIcon iconName="ArrowBackUp" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {!eventMode && !foundation && valueAt(path) && !locked && (
        <ActionIcon size="md" variant="subtle" color="gray" onClick={() => setRole(path, null)} aria-label={t("common:buttons.clear", { defaultValue: "Clear" })}>
          <AppIcon iconName="X" size={14} />
        </ActionIcon>
      )}
    </Group>
  );

  /** A foundation colour: always set (no Clear — the site needs one), carrying its linked role colours. */
  const foundationColor = (path: string, label: string, hint: string, opacity = true) => (
    <Box>
      {labelRow(path, label, true)}
      <ColorPickerInput
        name={path}
        size="md"
        description={hint}
        value={valueAt(path)}
        onChange={(_n, c) => setColor(path, c || null)}
        // Opacity (owner) — a see-through card over the background picture, a softer line. ⓘ Not the page background:
        // nothing is behind it but the browser's white, and its light / dark label would no longer say what guests see.
        allowTransparent={opacity}
        disabled={locked}
        error={fieldErrors[path]}
      />
    </Box>
  );

  /** A role colour: optional — empty falls back to the foundation colour named in `fallback`. */
  const roleColor = (path: string, label: string, fallback: string) => (
    <Box>
      {labelRow(path, label, false)}
      <ColorPickerInput
        name={path}
        size="md"
        description={fallback}
        value={valueAt(path)}
        onChange={(_n, c) => setRole(path, c || null)}
        allowTransparent
        disabled={locked}
        error={fieldErrors[path]}
      />
    </Box>
  );

  /**
   * A font: the heading / text faces (`font.display` / `font.body` — foundation, the stored title family follows the
   * heading one) or the EVENT TITLE's own (`element.title.family` — optional: cleared, the title uses the heading font).
   */
  const fontSelect = (path: "font.display" | "font.body" | "element.title.family", label: string, hint: string) => {
    const role: ThemeFontRole = path === "font.body" ? "body" : "display";
    const isTitle = path === "element.title.family";
    const stack = valueAt(path) ?? "";
    const key = themeFontKeyOfStack(stack);
    const data = themeFontSelectData(role);
    // A stack the catalog does not know (typed through the API) is shown as is, so opening the editor loses nothing.
    const withCurrent =
      stack && !key ? [{ group: t("themes:editor.currentFont", { defaultValue: "Current" }), items: [{ value: CUSTOM_FONT, label: stack }] }, ...data] : data;

    return (
      <Box>
        {labelRow(path, label, !isTitle)}
        <SelectField
          size="md"
          description={hint}
          data={withCurrent}
          value={key ?? (stack ? CUSTOM_FONT : null)}
          onChange={(_name, v) => {
            const font = themeFontOf(v);

            if (isTitle) {
              if (v !== CUSTOM_FONT) {
                setRole(path, font?.stack ?? (eventMode ? baseAt(path) : null));
              }

              return;
            }

            if (font) {
              setColor(path, font.stack);
            }
          }}
          placeholder={isTitle ? t("themes:editor.titleFontSame", { defaultValue: "Same as the heading font" }) : undefined}
          clearable={isTitle && !eventMode}
          searchable
          allowDeselect={isTitle && !eventMode}
          disabled={locked}
          error={fieldErrors[path]}
          aria-label={label}
          // Each face is shown in itself — the page declares every hosted face (BlockFontFaces).
          renderOption={({ option }) => (
            <Text size="md" style={{ fontFamily: option.value === CUSTOM_FONT ? stack : themeFontOf(option.value)?.stack }}>
              {option.label}
            </Text>
          )}
          styles={{ input: { fontFamily: stack || undefined } }}
          nothingFoundMessage={t("events:website.blockFontNone", { defaultValue: "No font by that name" })}
        />
      </Box>
    );
  };

  const swatch = (path: string) => {
    const color = valueAt(path);

    return color ? (
      <Box
        key={path}
        aria-hidden
        style={{ width: 16, height: 16, flex: "none", borderRadius: "50%", background: color, border: "1px solid var(--mantine-color-default-border)" }}
      />
    ) : null;
  };

  const fontName = (path: string): string | null => {
    const stack = valueAt(path);

    return stack ? themeFontOf(themeFontKeyOfStack(stack))?.label ?? stack.split(",")[0].replace(/'/g, "") : null;
  };

  /** One collapsible section: a title, what it holds at a glance, its hint, its controls. */
  const section = (key: string, title: string, summary: React.ReactNode, hint: string, children: React.ReactNode) => (
    <Accordion.Item value={key} key={key}>
      <Accordion.Control>
        <Group justify="space-between" wrap="nowrap" gap="sm" pr="xs">
          <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
            <Text fw={600} size="md" truncate>
              {title}
            </Text>
            {sectionAdjusted(key) && (
              <Badge size="md" variant="light" color="grape">
                {t("eventTheme:adjusted", { defaultValue: "Adjusted" })}
              </Badge>
            )}
          </Group>
          <Group gap={4} wrap="nowrap" style={{ flex: "none", maxWidth: "50%", overflow: "hidden" }}>
            {summary}
          </Group>
        </Group>
      </Accordion.Control>
      <Accordion.Panel>
        <Stack gap="md">
          <Text size="md" c="dimmed">
            {hint}
          </Text>
          {children}
        </Stack>
      </Accordion.Panel>
    </Accordion.Item>
  );

  const swatches = (key: string) => <>{THEME_LOOK_SECTION_FIELDS[key].map(swatch)}</>;
  // The headings' size (`font.headingSize`, 2026-10-25): Default is stored as nothing — except on an EVENT over a theme
  // with another size, where clearing the adjustment would only bring the theme's back.
  const setHeadingSize = (key: string) =>
    setRole("font.headingSize", key !== "md" ? key : eventMode && (baseAt("font.headingSize") ?? "md") !== "md" ? "md" : null);
  // 2026-10-24 — the pictures' look (`effects.media`): absent = colour. ⓘ On an EVENT over a black & white theme, colour
  // is stored as 'color' — clearing the adjustment would only put the theme's black and white back.
  const mediaGray = valueAt("effects.media") === "grayscale";
  const setMedia = (gray: boolean) =>
    setRole("effects.media", gray ? "grayscale" : eventMode && baseAt("effects.media") === "grayscale" ? "color" : null);
  const mediaLabel = (gray: boolean) =>
    gray ? t("themes:editor.mediaGray", { defaultValue: "Black and white" }) : t("themes:editor.mediaColor", { defaultValue: "In colour" });
  const fonts = [fontName("font.display"), fontName("font.body")].filter(Boolean).join(" · ") || null;
  const followsAccent = t("themes:editor.followsAccent", { defaultValue: "Empty: the accent." });

  return (
    <>
      {section(
        "page",
        t("themes:editor.page", { defaultValue: "Page" }),
        swatches("page"),
        t("themes:editor.pageHint", { defaultValue: "The ground everything sits on, and the cards on it." }),
        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
          {foundationColor("color.background", t("themes:editor.background", { defaultValue: "Page background" }), t("themes:editor.backgroundHint", { defaultValue: "Light or dark — it decides how the theme is labelled." }), false)}
          {foundationColor("color.surface", t("themes:editor.surface", { defaultValue: "Cards" }), t("themes:editor.surfaceHint", { defaultValue: "Cards, tickets, panels." }))}
          {foundationColor("color.surfaceAlt", t("themes:editor.surfaceAlt", { defaultValue: "Soft background" }), t("themes:editor.surfaceAltHint", { defaultValue: "The footer, chips, quiet tiles." }))}
        </SimpleGrid>,
      )}

      {section(
        "text",
        t("themes:editor.text", { defaultValue: "Text and lines" }),
        swatches("text"),
        t("themes:editor.textHint", { defaultValue: "Keep every text colour easy to read on the page and the cards." }),
        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
          {foundationColor("color.ink", t("themes:editor.ink", { defaultValue: "Text" }), t("themes:editor.inkHint", { defaultValue: "Headings and body text." }))}
          {foundationColor("color.inkSoft", t("themes:editor.inkSoft", { defaultValue: "Secondary text" }), t("themes:editor.inkSoftHint", { defaultValue: "Descriptions, subtitles." }))}
          {foundationColor("color.muted", t("themes:editor.muted", { defaultValue: "Muted text" }), t("themes:editor.mutedHint", { defaultValue: "Labels, captions, placeholders." }))}
          {foundationColor("color.line", t("themes:editor.line", { defaultValue: "Lines" }), t("themes:editor.lineHint", { defaultValue: "Hairline borders." }))}
          {foundationColor("color.lineStrong", t("themes:editor.lineStrong", { defaultValue: "Strong lines" }), t("themes:editor.lineStrongHint", { defaultValue: "Poll choices, totals — and the second button's border, unless you give it its own." }))}
        </SimpleGrid>,
      )}

      {section(
        "accent",
        t("themes:editor.accent", { defaultValue: "Accent" }),
        swatches("accent"),
        t("themes:editor.accentHint", {
          defaultValue: "The one brand colour — buttons, links and section labels follow it unless you give them their own below.",
        }),
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          {foundationColor("color.accent", t("themes:editor.accentColor", { defaultValue: "Accent" }), t("themes:editor.accentColorHint", { defaultValue: "Main buttons, links, highlights." }))}
          {foundationColor("color.accentInk", t("themes:editor.accentInk", { defaultValue: "Text on the accent" }), t("themes:editor.accentInkHint", { defaultValue: "Must read clearly on the accent." }))}
          {foundationColor("color.accentWash", t("themes:editor.accentWash", { defaultValue: "Accent tint" }), t("themes:editor.accentWashHint", { defaultValue: "A pale band or chip of the accent." }))}
          {foundationColor("color.accentLine", t("themes:editor.accentLine", { defaultValue: "Accent border" }), t("themes:editor.accentLineHint", { defaultValue: "The border of accent-tinted things." }))}
        </SimpleGrid>,
      )}

      {section(
        "type",
        t("themes:editor.type", { defaultValue: "Fonts and corners" }),
        fonts ? (
          <Text size="md" c="dimmed" truncate>
            {fonts}
          </Text>
        ) : null,
        t("themes:editor.typeHint", {
          defaultValue: "The hosted fonts load only on sites that use them. Fonts for headings only (script, fun, handwritten) are not offered for text.",
        }),
        <Stack gap="md">
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            {fontSelect("font.display", t("themes:editor.fontDisplay", { defaultValue: "Heading font" }), t("themes:editor.fontDisplayHint", { defaultValue: "Headings and big numbers." }))}
            {fontSelect("element.title.family", t("themes:editor.fontTitle", { defaultValue: "Event title font" }), t("themes:editor.fontTitleHint", { defaultValue: "The event's name at the top of the site and in its header. Empty: the heading font." }))}
            {fontSelect("font.body", t("themes:editor.fontBody", { defaultValue: "Text font" }), t("themes:editor.fontBodyHint", { defaultValue: "Everything else, forms and buttons included." }))}
            {/* 2026-10-25 — the headings' size: the SAME scale and labels as a block's Text size, and they multiply. */}
            <Box>
              {labelRow("font.headingSize", t("themes:editor.headingSize", { defaultValue: "Heading size" }), false)}
              <SelectField
                name="font.headingSize"
                // The label is the row above; this titles the full-screen picker on a phone.
                aria-label={t("themes:editor.headingSize", { defaultValue: "Heading size" })}
                size="md"
                description={t("themes:editor.headingSizeHint", {
                  defaultValue: "Every heading on the site, block titles included. A block's own Text size grows or shrinks them further.",
                })}
                data={BLOCK_TEXT_SIZES.map((s) => ({ value: s.key, label: s.label }))}
                value={valueAt("font.headingSize") ?? "md"}
                allowDeselect={false}
                onChange={(_name, v) => setHeadingSize(v ? String(v) : "md")}
                disabled={locked}
                error={fieldErrors["font.headingSize"]}
              />
            </Box>
          </SimpleGrid>
          <Box>
            {labelRow("shape.radius", t("themes:editor.radius", { defaultValue: "Corners" }), false)}
            <Input.Wrapper
              size="md"
              description={t("themes:editor.radiusHint", { defaultValue: "How round cards, fields, buttons and coloured blocks are. The event's main actions at the top stay pill-shaped." })}
              error={fieldErrors["shape.radius"]}
            >
              {/* Inset, so the end marks' labels stay inside the card. */}
              <Box px="lg">
                <Slider
                  mt="xs"
                  mb="xl"
                  min={0}
                  max={RADIUS_MAX}
                  step={1}
                  value={radiusOf(tokens)}
                  onChange={(n) => setRole("shape.radius", `${n}px`)}
                  label={(v) => `${v}px`}
                  marks={[
                    { value: 0, label: t("themes:editor.radiusSquare", { defaultValue: "Square" }) },
                    { value: 20, label: "20px" },
                    { value: RADIUS_MAX, label: t("themes:editor.radiusRound", { defaultValue: "Round" }) },
                  ]}
                  disabled={locked}
                  aria-label={t("themes:editor.radius", { defaultValue: "Corners" })}
                />
              </Box>
            </Input.Wrapper>
          </Box>
        </Stack>,
      )}

      {section(
        "buttons",
        t("themes:editor.buttons", { defaultValue: "Buttons" }),
        swatches("buttons"),
        t("themes:editor.buttonsHint", {
          defaultValue: "The main button is RSVP, Buy tickets and every other filled button; the second is My RSVP, My tickets and other quiet buttons. Clear a colour to use the theme's.",
        }),
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          {roleColor("button.primary.bg", t("themes:editor.primaryBg", { defaultValue: "Main button" }), followsAccent)}
          {roleColor("button.primary.text", t("themes:editor.primaryText", { defaultValue: "Main button text" }), t("themes:editor.followsAccentInk", { defaultValue: "Empty: the text on the accent." }))}
          {roleColor("button.secondary.bg", t("themes:editor.secondaryBg", { defaultValue: "Second button" }), t("themes:editor.followsCards", { defaultValue: "Empty: the card colour. Transparent is allowed." }))}
          {roleColor("button.secondary.text", t("themes:editor.secondaryText", { defaultValue: "Second button text" }), t("themes:editor.followsInk", { defaultValue: "Empty: the text colour." }))}
          {roleColor("button.secondary.border", t("themes:editor.secondaryBorder", { defaultValue: "Second button border" }), t("themes:editor.followsLine", { defaultValue: "Empty: the lines colour." }))}
        </SimpleGrid>,
      )}

      {section(
        "links",
        t("themes:editor.links", { defaultValue: "Links and section labels" }),
        swatches("links"),
        t("themes:editor.linksHint", { defaultValue: "Links include the site's page menu. Section labels are the small capitals above a block's heading." }),
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          {roleColor("element.link.color", t("themes:editor.link", { defaultValue: "Links" }), followsAccent)}
          {roleColor("element.eyebrow.color", t("themes:editor.eyebrow", { defaultValue: "Section labels" }), followsAccent)}
        </SimpleGrid>,
      )}

      {section(
        "media",
        t("themes:editor.media", { defaultValue: "Pictures and videos" }),
        <Text size="md" c="dimmed" truncate>
          {mediaLabel(mediaGray)}
        </Text>,
        t("themes:editor.mediaHint", {
          defaultValue: "Black and white greys every picture and video on the site — the cover, the blocks' pictures and backgrounds, people's photos. Maps keep their colour.",
        }),
        <Box>
          {labelRow("effects.media", t("themes:editor.mediaLook", { defaultValue: "Pictures and videos" }), false)}
          <SegmentedField
            name="effects.media"
            value={mediaGray ? "grayscale" : "color"}
            onChange={(_name, value) => setMedia(value === "grayscale")}
            data={[
              { label: mediaLabel(false), value: "color" },
              { label: mediaLabel(true), value: "grayscale" },
            ]}
            disabled={locked}
            error={fieldErrors["effects.media"]}
          />
        </Box>,
      )}
    </>
  );
};
